import React, { useMemo } from "react";
import Typography from "@mui/material/Typography";
import { Box, Grid, Stack, useMediaQuery } from "@mui/material";
import { IndianCurrency } from "app/utils/helper";

const headerCellSx = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#0c2f49",
};

const bodyCellSx = {
  fontSize: "14px",
  fontWeight: 500,
  color: "#0c2f49",
};

const multiplierTextSx = {
  fontSize: "12px",
  color: "#49627a",
};

const accentValueSx = {
  fontSize: "14px",
  fontWeight: 600,
};

const getComputedAmount = (amount, count) =>
  IndianCurrency(Number(count ?? 0) * Number(amount ?? 0));

const ShowFeeStructure = ({ feeData }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const feeList = useMemo(() => feeData?.list ?? [], [feeData?.list]);

  const totals = useMemo(
    () =>
      feeList.reduce(
        (acc, item) => {
          const count = Number(item?.count ?? 0);
          acc.totalFee += Number(item?.generalFeeAmount ?? 0) * count;
          acc.totalDiscount +=
            Number(item?.categoryDiscountAmount ?? 0) * count;
          acc.totalFinalFee += Number(item?.feeAmount ?? 0) * count;
          return acc;
        },
        { totalFee: 0, totalDiscount: 0, totalFinalFee: 0 },
      ),
    [feeList],
  );

  return (
    <>
      <Box
        sx={{
          border: "1px solid #d0d0e2",
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: "#fff",
        }}
      >
        {!isMobile && (
          <Grid
            container
            sx={{
              p: 0.75,
              bgcolor: "#f4f7fb",
              borderBottom: "1px solid #d0d0e2",
            }}
          >
            <Grid item xs={3}>
              <Typography sx={headerCellSx}>Fee Type</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography sx={headerCellSx}>General Fee Amount</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography sx={{ ...headerCellSx, color: "#F0C62E" }}>
                Category Discount
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography sx={{ ...headerCellSx, color: "#2789FD" }}>
                Final Fee Amount
              </Typography>
            </Grid>
          </Grid>
        )}
        {feeList.length === 0 && (
          <Typography sx={{ p: 2, textAlign: "center", ...bodyCellSx }}>
            No Fee Structure Found
          </Typography>
        )}
        {isMobile
          ? // Mobile Card View
            feeList.map((item, i) => (
              <Box
                key={`${item?.feeType || "fee"}-${i}`}
                sx={{
                  borderBottom:
                    i !== feeList.length - 1 ? "1px solid #e8edf3" : "none",
                  p: 1,
                  bgcolor: "#f9fbfd",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    mb: 1,
                    color: "#0c2f49",
                  }}
                >
                  {item?.feeType}
                </Typography>

                <Stack spacing={0.5}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                      General Fee:
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={0.3}>
                      <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                        {IndianCurrency(item?.generalFeeAmount)}
                      </Typography>
                      <Typography sx={multiplierTextSx}>
                        x{item?.count} ={" "}
                        {getComputedAmount(item?.generalFeeAmount, item?.count)}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                      Discount:
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={0.3}>
                      <Typography sx={{ ...accentValueSx, color: "#F0C62E" }}>
                        {IndianCurrency(item?.categoryDiscountAmount)}
                      </Typography>
                      <Typography sx={multiplierTextSx}>
                        x{item?.count} ={" "}
                        {getComputedAmount(
                          item?.categoryDiscountAmount,
                          item?.count,
                        )}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                      Final Fee:
                    </Typography>
                    <Stack direction="row" alignItems="center" gap={0.3}>
                      <Typography sx={{ ...accentValueSx, color: "#2789FD" }}>
                        {IndianCurrency(item?.feeAmount)}
                      </Typography>
                      <Typography sx={multiplierTextSx}>
                        x{item?.count} ={" "}
                        {getComputedAmount(item?.feeAmount, item?.count)}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Box>
            ))
          : // Desktop Table View
            feeList.map((item, i) => (
              <Grid
                container
                sx={{
                  p: 1,
                  borderBottom:
                    i !== feeList.length - 1 ? "1px solid #e8edf3" : "none",
                }}
                key={`${item?.feeType || "fee"}-${i}`}
              >
                <Grid item xs={3}>
                  <Typography sx={bodyCellSx}>{item?.feeType}</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Stack direction={"row"} alignItems={"flex-end"} gap={0.3}>
                    <Typography sx={bodyCellSx}>
                      {IndianCurrency(item?.generalFeeAmount)}
                    </Typography>
                    <Typography sx={multiplierTextSx}>
                      x{item?.count} ={" "}
                      <Box component="span" sx={bodyCellSx}>
                        {getComputedAmount(item?.generalFeeAmount, item?.count)}
                      </Box>
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={3}>
                  <Stack direction={"row"} alignItems={"flex-end"} gap={0.3}>
                    <Typography sx={{ ...bodyCellSx, color: "#F0C62E" }}>
                      {IndianCurrency(item?.categoryDiscountAmount)}
                    </Typography>
                    <Typography sx={multiplierTextSx}>
                      x{item?.count} ={" "}
                      <Box component="span" sx={bodyCellSx}>
                        {getComputedAmount(
                          item?.categoryDiscountAmount,
                          item?.count,
                        )}
                      </Box>
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={3}>
                  <Stack direction={"row"} alignItems={"flex-end"} gap={0.3}>
                    <Typography sx={{ ...bodyCellSx, color: "#2789FD" }}>
                      {IndianCurrency(item?.feeAmount)}
                    </Typography>

                    <Typography sx={multiplierTextSx}>
                      x{item?.count} ={" "}
                      <Box component="span" sx={bodyCellSx}>
                        {getComputedAmount(item?.feeAmount, item?.count)}
                      </Box>
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            ))}
      </Box>
      {feeList.length > 0 && (
        <Stack
          direction="row"
          alignItems={"center"}
          justifyContent={isMobile ? "center" : "flex-end"}
          sx={{ mt: 1 }}
        >
          <Box
            p={isMobile ? 1 : 0.5}
            sx={{
              border: "1px solid #d0d0e2",
              display: "inline-block",
              width: isMobile ? "100%" : "auto",
              borderRadius: "8px",
            
            }}
          >
            <Stack
              direction={isMobile ? "column" : "row"}
              alignItems={isMobile ? "stretch" : "center"}
              justifyContent={"center"}
              gap={isMobile ? 1 : 2}
            >
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={isMobile ? "space-between" : "flex-start"}
                gap={1}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                  Total Fee:
                </Typography>
                <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
                  {IndianCurrency(totals.totalFee)}
                </Typography>
              </Stack>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={isMobile ? "space-between" : "flex-start"}
                gap={1}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                  Total Discount:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#F0C62E",
                  }}
                >
                  {IndianCurrency(totals.totalDiscount)}
                </Typography>
              </Stack>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={isMobile ? "space-between" : "flex-start"}
                gap={1}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
                  Final Fee:
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#2789FD",
                  }}
                >
                  {IndianCurrency(totals.totalFinalFee)}
                </Typography>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      )}
    </>
  );
};

export default ShowFeeStructure;
